import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Bell, CheckCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { rowsOf } from '@/Hooks/useApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState, ErrorState } from '@/components/ui/states';
import { cn } from '@/lib/utils';
import { apiMessage, formatDateTime } from './lib';

interface InboxItem {
  id: string;
  type: string;
  title: string;
  body: string | null;
  refType: string | null;
  refId: string | null;
  readAt: string | null;
  createdAt: string;
}

/** How often the badge re-reads its count while the panel is shut. */
const POLL_MS = 60_000;

/**
 * §27 — the signed-in user's own notification inbox, in the header.
 *
 * The count endpoint is a single `count(*)`, so it is what polls; the list is
 * only fetched when the panel is opened. Neither route carries a permission key
 * — an inbox is scoped to its owner by the server, so every signed-in user has
 * one and the bell is always present.
 */
export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState<InboxItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const loadCount = useCallback(async () => {
    try {
      const { data } = await api.get<{ unread: number }>('/notifications/inbox/unread-count');
      setUnread(data?.unread ?? 0);
    } catch {
      // A failing badge must never break the header it lives in.
    }
  }, []);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/notifications/inbox', {
        params: { limit: 25, sortBy: 'createdAt', sortOrder: 'desc' },
      });
      setItems(rowsOf<InboxItem>(data).rows);
    } catch (e) {
      setError(apiMessage(e, t('platform.inbox.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void loadCount();
    const id = window.setInterval(() => void loadCount(), POLL_MS);
    return () => window.clearInterval(id);
  }, [loadCount]);

  useEffect(() => {
    if (open) void loadItems();
  }, [open, loadItems]);

  // Close on an outside click or Escape, the way the header's other menus do.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!panelRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const markRead = async (item: InboxItem) => {
    if (item.readAt) return;
    setBusy(true);
    try {
      await api.patch(`/notifications/inbox/${item.id}/read`);
      setItems((prev) =>
        prev.map((n) => (n.id === item.id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      setUnread((n) => Math.max(0, n - 1));
    } catch (e) {
      toast.error(apiMessage(e, t('platform.inbox.markFailed')));
    } finally {
      setBusy(false);
    }
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.patch('/notifications/inbox/read-all');
      const stamp = new Date().toISOString();
      setItems((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: stamp })));
      setUnread(0);
    } catch (e) {
      toast.error(apiMessage(e, t('platform.inbox.markFailed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('platform.inbox.title')}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="relative inline-flex">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              className="absolute -top-1.5 grid min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[10px] font-semibold leading-4 text-white"
              style={{ insetInlineEnd: '-0.375rem' }}
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </span>
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label={t('platform.inbox.title')}
          className="absolute top-full z-40 mt-2 flex max-h-[70dvh] w-[min(22rem,90vw)] flex-col rounded-xl border bg-surface shadow-xl"
          style={{ insetInlineEnd: 0 }}
        >
          <header className="flex items-center justify-between gap-2 border-b p-3">
            <h2 className="text-sm font-semibold">{t('platform.inbox.title')}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => void markAllRead()}
              disabled={busy || unread === 0}
            >
              <CheckCheck className="h-4 w-4" />
              {t('platform.inbox.markAllRead')}
            </Button>
          </header>

          <div className="scrollbar-thin flex-1 overflow-y-auto">
            {loading && !items.length ? (
              <p className="p-6 text-center text-sm text-ink-muted">
                {t('common.loading')}
              </p>
            ) : error ? (
              <ErrorState message={error} onRetry={() => void loadItems()} />
            ) : !items.length ? (
              <EmptyState
                title={t('platform.inbox.empty')}
                hint={t('platform.inbox.emptyHint')}
              />
            ) : (
              <ul>
                {items.map((item) => (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => void markRead(item)}
                      disabled={busy || !!item.readAt}
                      className={cn(
                        'flex w-full flex-col gap-1 border-b p-3 text-start last:border-0',
                        item.readAt ? 'opacity-70' : 'bg-accent-soft/40 hover:bg-accent-soft',
                      )}
                    >
                      <span className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium">{item.title}</span>
                        {!item.readAt && <Badge tone="info">{t('platform.inbox.new')}</Badge>}
                      </span>
                      {item.body && (
                        <span className="text-xs text-ink-muted">{item.body}</span>
                      )}
                      <span className="text-xs text-ink-muted">
                        {formatDateTime(item.createdAt, i18n.language)}
                        {item.refType ? ` · ${item.refType}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
